import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AlertsType } from '@rappi/ui/alerts';
import { of } from 'rxjs';

import { AlertsService } from '../../../../services/alerts/alerts.service';
import { StoreAuxService } from '../../../../services/storeAux/store-aux.service';
import { MakersFinancesMgrService } from '../../../services/makers-finances-mgr.service';
import {
  PoDocumentPreviewDialogComponent,
} from '../../purchase-order-detail/po-document-preview-dialog/po-document-preview-dialog.component';
import {
  FILES_CARD_ERROR_TYPE,
  FilesCardError,
  FilesCardErrorType,
  FilesCardLabels,
  FilesCardMessages,
  PersistAddedFn,
  PersistRemovedFn,
  UploadedFileValue,
} from './files-card.types';

interface RejectedFile {
  name: string;
  reason: string;
  type: FilesCardErrorType;
}

// Generic multi-file dropzone + uploaded-file list, synced to a form control that holds an
// UploadedFileValue[]. Extracted from CompanyFilesCardComponent (see that wrapper for the
// company persistence hooks and SectionPoDocumentComponent for the PO local-only flow).
// Renders NO section shell — title, banners and section chrome belong to the wrappers.
//
// Validation is extension-primary + mime-secondary (a file passes if EITHER matches): browsers
// report file.type unreliably, so the extension is the trusted signal. maxSizeBytes/maxFiles 0
// mean "no limit".
@Component({
  selector: 'app-files-card',
  templateUrl: './files-card.component.html',
  styleUrls: ['./files-card.component.scss'],
})
export class FilesCardComponent {
  @Input() form: FormGroup;
  @Input() controlName = '';
  @Input() acceptExtensions: string[] = [];
  @Input() acceptMimes: string[] = [];
  @Input() maxSizeBytes = 0;
  @Input() maxFiles = 0;
  @Input() multiple = true;
  @Input() required = false;
  @Input() submitted = false;
  @Input() readOnly = false;
  @Input() showDownload = false;
  @Input() showRequiredLabel = true;
  @Input() externalBusy = false;
  @Input() labels: FilesCardLabels;
  @Input() messages: FilesCardMessages;
  @Input() persistAdded?: PersistAddedFn;
  @Input() persistRemoved?: PersistRemovedFn;
  @Output() validationError = new EventEmitter<FilesCardError>();
  // Fires after every successful add/remove (the control's new list) — lets a wrapper clear
  // sticky error state once the user recovers (see SectionPoDocumentComponent.onFilesChanged).
  @Output() filesChanged = new EventEmitter<UploadedFileValue[]>();

  isDragging = false;
  uploading = false;
  deleting = false;
  isPreviewing = false;
  isDownloading = false;
  rejected: RejectedFile[] = [];

  constructor(
    private readonly financesService: MakersFinancesMgrService,
    private readonly storeAux: StoreAuxService,
    private readonly alerts: AlertsService,
    private readonly dialog: MatDialog,
  ) {}

  get files(): UploadedFileValue[] {
    return (this.form?.get(this.controlName)?.value as UploadedFileValue[] | null) ?? [];
  }

  get acceptAttr(): string {
    return this.acceptExtensions.map(extension => `.${extension}`).join(',');
  }

  get showRequiredError(): boolean {
    return this.submitted && this.required && !this.files.length;
  }

  get busy(): boolean {
    return this.uploading || this.deleting || this.externalBusy;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(): void {
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    this.addFiles(event.dataTransfer?.files ?? null);
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.addFiles(input.files);
    // Reset so selecting the same file again still fires a change event.
    input.value = '';
  }

  addFiles(list: FileList | null): void {
    if (this.readOnly || !list || !list.length) {
      return;
    }

    const incoming = Array.from(list);
    const accepted: File[] = [];
    this.rejected = [];

    incoming.forEach(file => {
      const rejection = this.validate(file);
      if (rejection) {
        this.rejected.push({ name: file.name, reason: rejection.reason, type: rejection.type });
      } else {
        accepted.push(file);
      }
    });

    if (this.rejected.length) {
      // One aggregated alert rather than one toast per rejected file. The emitted type is the
      // FIRST rejection's, so a size rejection surfaces the size banner, not the format one.
      this.alerts.openAlerts(this.rejected[0].reason, AlertsType.error);
      this.validationError.emit({
        type: this.rejected[0].type,
        files: this.rejected.map(item => item.name),
      });
    }
    if (!accepted.length) {
      return;
    }

    const toUpload = this.multiple ? accepted : accepted.slice(0, 1);

    // Client-side pre-check mirroring the backend's "current + new <= max" rule — avoids an
    // upload round trip the add would just reject anyway. The backend stays authoritative.
    if (this.wouldExceedMaxFiles(toUpload.length)) {
      this.alerts.openAlerts(this.messages.maxFilesExceeded, AlertsType.error);
      this.validationError.emit({
        type: FILES_CARD_ERROR_TYPE.MAX_FILES_EXCEEDED,
        files: toUpload.map(file => file.name),
      });
      return;
    }

    this.uploading = true;
    this.financesService.uploadFiles(toUpload, this.storeAux.country).subscribe({
      next: uploaded => {
        const now = new Date().toISOString();
        const added: UploadedFileValue[] = uploaded.map((data, index) => ({
          key: data.key,
          name: toUpload[index].name,
          uploadedAt: now,
        }));
        this.runPersistAdded(added);
      },
      error: () => {
        this.uploading = false;
        this.alerts.openAlerts(this.messages.uploadFailed, AlertsType.error);
        this.validationError.emit({
          type: FILES_CARD_ERROR_TYPE.UPLOAD_FAILED,
          files: toUpload.map(file => file.name),
        });
      },
    });
  }

  removeFile(index: number): void {
    const file = this.files[index];
    if (!file || this.readOnly) {
      return;
    }

    this.deleting = true;
    const op = this.persistRemoved
      ? this.persistRemoved(file, this.files)
      : of(this.files.filter((_f, i) => i !== index));
    op.subscribe({
      next: nextFiles => {
        this.deleting = false;
        this.setValue(nextFiles);
        this.alerts.openAlerts(this.messages.removed, AlertsType.success);
      },
      error: error => {
        this.deleting = false;
        this.alerts.openAlerts(this.errorMessage(error, this.messages.removeFailed), AlertsType.error);
      },
    });
  }

  openPreview(file: UploadedFileValue): void {
    if (this.isPreviewing) {
      return;
    }
    this.isPreviewing = true;
    this.financesService.previewFile(file.key, this.storeAux.country).subscribe({
      next: blob => {
        this.isPreviewing = false;
        const url = URL.createObjectURL(blob);
        this.dialog.open(PoDocumentPreviewDialogComponent, {
          width: '95vw',
          maxWidth: '95vw',
          height: '90vh',
          panelClass: 'podp-panel',
          data: { url, name: file.name, mimeType: blob.type },
        });
      },
      error: () => {
        this.isPreviewing = false;
        this.alerts.openAlerts(this.messages.uploadFailed, AlertsType.error);
      },
    });
  }

  downloadFile(file: UploadedFileValue): void {
    if (this.isDownloading) {
      return;
    }
    this.isDownloading = true;
    this.financesService.previewFile(file.key, this.storeAux.country).subscribe({
      next: blob => {
        this.isDownloading = false;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name || 'document';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      },
      error: () => {
        this.isDownloading = false;
        this.alerts.openAlerts(this.messages.uploadFailed, AlertsType.error);
      },
    });
  }

  trackByKey(_index: number, file: UploadedFileValue): string {
    return file.key;
  }

  private runPersistAdded(added: UploadedFileValue[]): void {
    const op = this.persistAdded
      ? this.persistAdded(added, this.files)
      : of(this.multiple ? [...this.files, ...added] : added);
    op.subscribe({
      next: nextFiles => {
        this.uploading = false;
        this.setValue(nextFiles);
        this.alerts.openAlerts(this.messages.uploadComplete, AlertsType.success);
      },
      error: error => {
        this.uploading = false;
        this.alerts.openAlerts(this.errorMessage(error, this.messages.uploadFailed), AlertsType.error);
      },
    });
  }

  // `file.type` is empty or wrong in some browsers, so the extension is the primary check and the
  // mime list is a secondary accept. Returns the toast copy AND the error type so the wrapper's
  // banner matches the actual rejection reason.
  private validate(file: File): { reason: string; type: FilesCardErrorType } | null {
    const extension = (file.name.split('.').pop() ?? '').toLowerCase();
    const allowedExtensions = this.acceptExtensions.map(value => value.toLowerCase());
    const extensionOk = allowedExtensions.indexOf(extension) !== -1;
    const mimeOk = !!file.type && this.acceptMimes.indexOf(file.type) !== -1;
    if (!extensionOk && !mimeOk) {
      return { reason: this.messages.invalidFormat, type: FILES_CARD_ERROR_TYPE.INVALID_FORMAT };
    }
    if (this.maxSizeBytes && file.size > this.maxSizeBytes) {
      return { reason: this.messages.sizeExceeded, type: FILES_CARD_ERROR_TYPE.SIZE_EXCEEDED };
    }
    return null;
  }

  private wouldExceedMaxFiles(additionalCount: number): boolean {
    if (!this.maxFiles) {
      return false;
    }
    return this.files.length + additionalCount > this.maxFiles;
  }

  // Backend errors carry { error: { message } }; consumer-thrown guards (e.g. the company min
  // guard) carry a plain { message }. Both win over the fallback constant.
  private errorMessage(error: unknown, fallback: string): string {
    const body = (error as { error?: { message?: string } } | null)?.error;
    const message = body?.message ?? (error as { message?: string } | null)?.message;
    return typeof message === 'string' && message.trim() ? message : fallback;
  }

  private setValue(files: UploadedFileValue[]): void {
    const control = this.form?.get(this.controlName);
    control?.setValue(files);
    control?.markAsTouched();
    this.filesChanged.emit(files);
  }
}
